import { useState, useEffect } from 'react';
import { useHackathonTeam } from './useTeam';
import { useHackathonApplication } from './useApplication';
import { useHackathonCategories } from './useCategories';
import { useHackathonProgress } from './useProgress';
import { useMentorResources } from './useMentorResources';

export const useHackathonDashboard = (eventId, event, initialApplicationData = {}) => {
    // Application form state
    const [applicationData, setApplicationData] = useState({
        // Team Information
        teamName: "",
        teamSize: "",
        teamLeaderName: "",
        teamLeaderEmail: "",
        teamLeaderPhone: "",

        // Category & Availability
        category: "",
        availability: "",
        timeCommitment: "",

        // Pitch & Demo Requirements
        pitchDeckFile: null,
        pitchDeckUrl: "",
        pitchDeckFileName: "",
        youtubePitchDeck: "",
        projectDemoLink: "",
        socialMediaPosts: [""],

        // Source & Consent
        referralSource: "",
        agreeToTerms: false,
        consentToReview: false,
        consentToPublicity: false,
        commitToParticipate: false,
        ...initialApplicationData,
    });

    // UI state
    const [showDeadlineModal, setShowDeadlineModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Use custom hooks for data management
    const {
        currentTeam,
        teamMembers,
        isTeamLead,
        teamLeaderData,
        isLoading: teamLoading,
        error: teamError,
        updateTeam,
        markTeamAsApplied,
    } = useHackathonTeam(eventId);

    // Get mentor resources if team has a mentor
    const {
        mentorResources,
        isLoading: mentorResourcesLoading,
        error: mentorResourcesError
    } = useMentorResources(currentTeam?.mentorId, currentTeam?.id);

    const {
        userApplication,
        evaluationData,
        isLoading: applicationLoading,
        error: applicationError,
        submitApplication,
    } = useHackathonApplication(currentTeam, eventId);



    const {
        categories,
        isLoading: categoriesLoading,
        error: categoriesError,
    } = useHackathonCategories();

    const {
        progressData,
        showApplication,
        applicationWindowOpen,
        applicationWindowStart,
        applicationWindowEnd,
        getProgressStatus,
        recalculateProgress,
    } = useHackathonProgress(event, currentTeam, userApplication, evaluationData, applicationData, mentorResources);

    // Combined loading state
    const isDataLoading = teamLoading || applicationLoading || categoriesLoading || mentorResourcesLoading;

    // Combined error state
    const combinedError = teamError || applicationError || categoriesError || mentorResourcesError || error;

    // Update application data when team data changes
    useEffect(() => {
        if (currentTeam && teamLeaderData) {
            setApplicationData(prev => ({
                ...prev,
                teamName: currentTeam.name || "",
                teamSize: currentTeam.members?.length?.toString() || "",
                teamLeaderName: teamLeaderData?.displayName || "",
                teamLeaderEmail: teamLeaderData?.email || "",
                teamLeaderPhone: teamLeaderData?.phoneNumber || "",
            }));
        }
    }, [currentTeam, teamLeaderData]);


    // Application form handlers
    const handleApplicationDataChange = (field, value) => {
        const newData = { ...applicationData, [field]: value };
        setApplicationData(newData);

        // Recalculate progress with updated data
        recalculateProgress(newData);
    };

    const handleApplicationSubmit = async () => {
        if (!currentTeam) {
            setError("You must be part of a team to submit an application");
            return { success: false };
        }

        if (!isTeamLead) {
            setError("Only team leaders can submit applications");
            return { success: false };
        }

        setIsSubmitting(true);
        setError("");

        try {
            // Submit the application
            const result = await submitApplication(applicationData, applicationData.pitchDeckFile);

            if (result.success) {
                // Mark team as applied
                const teamUpdateResult = await markTeamAsApplied(currentTeam.id, result.applicationId);

                if (teamUpdateResult.success) {
                    setSuccessMessage("Application submitted successfully!");
                    setTimeout(() => setSuccessMessage(""), 5000);

                    // Clear sensitive form data but keep basic info
                    setApplicationData(prev => ({
                        ...prev,
                        pitchDeckFile: null,
                        youtubePitchDeck: "",
                        projectDemoLink: "",
                        socialMediaPosts: [""],
                        referralSource: "",
                        agreeToTerms: false,
                        consentToReview: false,
                        consentToPublicity: false,
                        commitToParticipate: false,
                    }));

                    return { success: true };
                } else {
                    setError("Application submitted but failed to update team status");
                    return { success: false };
                }
            } else {
                setError(result.error || "Failed to submit application");
                return { success: false };
            }
        } catch (err) {
            setError("An unexpected error occurred while submitting");
            console.error("Application submission error:", err);
            return { success: false };
        } finally {
            setIsSubmitting(false);
        }
    };

    // Team update handler
    const handleTeamUpdate = async (updateData) => {
        if (!currentTeam) return { success: false };

        return await updateTeam(currentTeam.id, updateData);
    };

    // Clear messages
    const clearSuccessMessage = () => setSuccessMessage("");
    const clearError = () => setError("");

    // Close deadline modal
    const closeDeadlineModal = () => setShowDeadlineModal(false);

    return {
        // Team data
        currentTeam,
        teamMembers,
        isTeamLead,
        teamLeaderData,

        // Application data
        userApplication,
        evaluationData,
        applicationData,

        // Categories
        categories,

        // Mentor Resources
        mentorResources,

        // Progress
        progressData,
        showApplication: true,
        applicationWindowOpen,
        applicationWindowStart,
        applicationWindowEnd,
        getProgressStatus,

        // Loading states
        isDataLoading,
        isSubmitting,

        // Error states
        error: combinedError,

        // UI state
        successMessage,
        showDeadlineModal,

        // Handlers
        handleApplicationDataChange,
        handleApplicationSubmit,
        handleTeamUpdate,
        clearSuccessMessage,
        clearError,
        closeDeadlineModal,
    };
};